import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  Account,
  ActivityFilterModel,
  ActivityOrderBy,
  cacheKeys, DayIntervalUnit,
  FeatureName,
  Issue,
  IssueMessageType,
  IssueType,
  Media,
  replaceMentionPlaceholderWithNames,
  Severity,
  useCreateMutation,
  useIsFeatureEnabled,
  useTogglePin,
  useTranslatedText,
} from '@gripp/shared-logic';
import { Pill, QrIcon } from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes';
import { StatusTag } from '../crop-circle/Components/Tag';
import { TypeIcon, type TypeVariant } from '../crop-circle/Components/Type';
import { typeLabels } from '../crop-circle/Components/Type/types';
import { Icon } from '../crop-circle/icons';
import { getIssueAuditData, getIssueFullName } from '../issue/issueFormatUtils';
import { IssueReminderModal } from '../issue/issueReminderModal';
import { useTypedMediaItem } from '../media/useTypedMediaItem';
import { IssueCardOld } from './issueCard-old';

export interface IssueCardData {
  type: string;
  asset: {
    name: string;
    coverImage?: {
      id?: string;
      url?: string;
      extension?: string;
      status?: string;
      type?: string;
    };
  };
  workspace: {
    name: string;
  };
  id: string;
  title: string;
  subtitle?: string;
  severity: Severity;
  createdAt: string;
  effectiveCreationDate?: string;
  isPinned?: boolean;
  updatedAt: string;
  labels?: {
    id: string;
    name: string;
  }[];
  nextReminderDate?: string;
  userMentions?: Account[];
  latestMessage: {
    text: string;
    createdBy: Account;
    userMentions: Account[];
    type: IssueMessageType;
  };
}

export type IssueCardProps = {
  account: Account | null;
  activity: IssueCardData;
  showAssetName?: boolean;
  isUnread?: boolean;
  isMentionedInInbox?: boolean;
  filter: ActivityFilterModel;
  borderRadius?: number;
  isSelected?: boolean;
};

// Get background color based on issue type
const getIssueTypeColor = (issueType?: string) => {
  const normalizedType = issueType?.toUpperCase();
  switch (normalizedType) {
    case 'ALERT':
      return '#FF5630'; // Red for alerts
    case 'FEEDBACK':
      return '#6554C0'; // Purple for feedback
    case 'NOTE':
      return '#36B37E'; // Green for notes
    case 'REPORT':
      return '#0052CC'; // Blue for reports
    case 'REMINDER':
      return '#FFAB00'; // Yellow for reminders
    case 'ROUTINE':
      return '#00B8D9'; // Cyan for routines
    default:
      return '#36B37E'; // Default green
  }
};

// Map IssueType to TypeVariant based on crop circle design
const getIssueTypeVariant = (issueType?: string): TypeVariant => {
  const normalizedType = issueType?.toUpperCase();
  switch (normalizedType) {
    case 'ALERT':
      return 'attention'; // ALERT: Red → attention
    case 'FEEDBACK':
      return 'note'; // FEEDBACK: Purple → note
    case 'NOTE':
      return 'done'; // NOTE: Green → done (exact color match #36B37E)
    case 'REPORT':
      return 'routines'; // REPORT: Blue → routines
    case 'REMINDER':
      return 'blocker'; // REMINDER: Yellow → blocker
    case 'ROUTINE':
      return 'routines'; // ROUTINE: Blue/Cyan → routines
    default:
      return 'done';
  }
};

// Right side underlay - shows issue type (revealed when swiping LEFT)
const IssueTypeUnderlay = ({ item, cardHeight }: { item: any; cardHeight: number }) => {
  const { t } = useTranslation();
  const issueType = item?.type || 'NOTE';
  const normalizedType = String(issueType).toUpperCase();
  
  const backgroundColor = getIssueTypeColor(normalizedType) || '#FF0000';
  const variant = getIssueTypeVariant(normalizedType);
  // Use translation for type label, fallback to typeLabels if translation key doesn't exist
  const translationKey = `issue.types.${variant}`;
  const translatedLabel = t(translationKey);
  const label = translatedLabel !== translationKey ? translatedLabel : (typeLabels[variant] || normalizedType);
  return (
    <View
      style={[
        styles.underlayRight,
        {
          width: cardHeight,
          backgroundColor: backgroundColor,
          alignItems: 'center',
          justifyContent: 'center',
        },
      ]}
    >
      <View style={styles.issueTypeUnderlayContent}>
        <TypeIcon variant={variant} size={16} color="white" />
        <Text style={styles.issueTypeUnderlayText}>
          {label}
        </Text>
      </View>
    </View>
  );
};

const CREATE_WORKFLOW = `mutation createWorkflow($input: CreateWorkflowInput!) {
  createWorkflow(input: $input) {
    id
  }
}`;

// Right side underlay - reminder and pin/unpin action buttons
const ActionButtonsUnderlay = ({ item, cardHeight }: { item: any; cardHeight: number }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { togglePin } = useTogglePin();
  const [isReminderModalVisible, setIsReminderModalVisible] = useState(false);

  const { mutateAsync: createWorkflow } = useCreateMutation({
    modelName: 'workflow',
    query: CREATE_WORKFLOW,
  });

  // Check if reminder is available (no existing reminder AND issue type is not REMINDER)
  const hasReminder = !!item?.nextReminderDate;
  const isReminderType = item?.type === IssueType.Reminder;
  const isReminderAvailable = !hasReminder && !isReminderType;

  const defaultValues: any = {
    alertType: '',
    inputField: '',
    days: 1,
    dayInterval: 1,
    dayIntervalUnit: DayIntervalUnit.Days,
    daysStartDate: moment().format('MM/DD/YYYY'),
    onDate: undefined,
    notes: '',
  };

  const submitReminder = async (input: any) => {
    await createWorkflow(
      { input },
      {
        onSuccess: async () => {
          setIsReminderModalVisible(false);
          // Refresh the workflow cache to show the new reminder
          queryClient.invalidateQueries({ queryKey: cacheKeys('workflow').all });
        },
      }
    );
  };

  const handleReminder = () => {
    setIsReminderModalVisible(true);
  };

  const handlePinUnpin = async () => {
    await togglePin(item.id, !item.isPinned);
  };

  const onReminderSuccess = () => {
    queryClient.invalidateQueries({ queryKey: cacheKeys('workflow').all });
  };

  // Convert item to Issue type for IssueReminderModal
  const issue: Issue = {
    id: item.id,
    workspace: item.workspace,
    type: item.type as IssueType,
    severity: item.severity,
    title: item.title,
    asset: item.asset,
  } as Issue;

  const buttonWidth = cardHeight || 200;
  
  return (
    <>
      <View style={styles.underlayRight}>
        {isReminderAvailable && (
          <TouchableOpacity
            style={[
              styles.underlayRightSquare1,
              {
                right: buttonWidth,
                width: buttonWidth,
              },
              styles.underlayActionButton,
            ]}
            onPress={handleReminder}
          >
            <View style={styles.underlayIconContainer}>
              <MaterialCommunityIcons name="alarm" color="white" size={24} />
              <Text style={styles.underlayText}>{t('issue.menu.reminder')}</Text>
            </View>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={[
            styles.underlayRightSquare2,
            {
              width: buttonWidth,
            },
            styles.underlayActionButton,
          ]}
          onPress={handlePinUnpin}
        >
          <View style={styles.underlayIconContainer}>
            <MaterialCommunityIcons
              name={item.isPinned ? 'pin-off' : 'pin'}
              color="white"
              size={24}
            />
            <Text style={styles.underlayText}>
              {item.isPinned ? t('issue.menu.unpinToAsset') : t('issue.menu.pinToAsset')}
            </Text>
          </View>
        </TouchableOpacity>
      </View>
      <IssueReminderModal
        defaultValues={defaultValues}
        issue={issue}
        visible={isReminderModalVisible}
        hide={() => setIsReminderModalVisible(false)}
        submitReminder={submitReminder}
        onSuccess={onReminderSuccess}
      />
    </>
  );
};

const useIssueCardViewModel = (props: IssueCardProps) => {
  const { t } = useTranslation();
  const { activity } = props;
  const [auditData, setAuditData] = useState({ who: '...', when: '...' });
  const [isHovered, setIsHovered] = useState(false);

  const coverImageCandidate = activity.asset?.coverImage as unknown;
  const coverImageForTypedMedia =
    isMediaLike(coverImageCandidate) ? (coverImageCandidate as Media) : undefined;

  const { typedMediaItem: typedCoverImage } =
    useTypedMediaItem(coverImageForTypedMedia);

  useEffect(() => {
    setAuditData(
      getIssueAuditData(
        activity as unknown as Issue,
        props.filter.orderBy as ActivityOrderBy
      )
    );
  }, [activity, props.filter.orderBy]);

  const getProcessedTitle = () => {
    const titleText = activity.title;

    if (titleText.includes('@[') && (activity as any).userMentions) {
      return replaceMentionPlaceholderWithNames(
        titleText,
        (activity as any).userMentions,
        t('mentions.unknownUser'),
        true
      );
    }

    return titleText;
  };

  const getLatestMessageText = () => {
    const latestMessage = activity?.latestMessage;

    if (!latestMessage) {
      return '';
    }

    const user = getIssueFullName(
      latestMessage.createdBy?.id,
      latestMessage.createdBy?.name
    );

    if (props.isUnread && props.isMentionedInInbox) {
      return (
        <Text style={{ color: Colors.primary, fontSize: 14 }}>
          {t('activity.list.youAreMentioned')}
        </Text>
      );
    }

    if (
      latestMessage.type === IssueMessageType.RoutineMessage ||
      latestMessage.type === IssueMessageType.RoutineExceptionMessage
    ) {
      return activity?.subtitle
        ? `${user} ${activity.subtitle}`
        : t('routines.message.messageTitle', { name: user }).slice(0, -1);
    }

    if (
      latestMessage.type === IssueMessageType.SystemMessage ||
      latestMessage.type === IssueMessageType.RoutinePromptMessage
    ) {
      const { translatedText } = useTranslatedText('text', latestMessage);
      return translatedText;
    }

    if (!latestMessage.text) {
      return `${user}: ${t('issue.latestMessage.media')}`;
    }

    const { translatedText } = useTranslatedText('text', latestMessage);
    const messageText = translatedText || latestMessage?.text;

    const processedText = messageText.includes('@[')
      ? replaceMentionPlaceholderWithNames(
          messageText,
          latestMessage?.userMentions,
          t('mentions.unknownUser'),
          true
        )
      : messageText;

    return `${user}: ${processedText}`;
  };

  const latestMessageContent = getLatestMessageText();

  const renderLatestMessage = (textStyle: any, numberOfLines = 1) => {
    if (typeof latestMessageContent === 'string') {
      return (
        <Text variant="bodyMedium" style={textStyle} numberOfLines={numberOfLines}>
          {latestMessageContent}
        </Text>
      );
    }

    return latestMessageContent;
  };

  const hoverProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }
      : {};

  return {
    activity,
    auditData,
    isHovered,
    hoverProps,
    typedCoverImage,
    getProcessedTitle,
    renderLatestMessage,
  };
};

function isMediaLike(value: unknown): value is Media {
  const v = value as any;
  return (
    !!v &&
    typeof v === 'object' &&
    typeof v.id === 'string' &&
    typeof v.extension === 'string' &&
    typeof v.type === 'string' &&
    typeof v.status === 'string'
  );
}

export const IssueCardBase = (props: IssueCardProps) => {
  const {
    activity,
    auditData,
    isHovered,
    hoverProps,
    typedCoverImage,
    getProcessedTitle,
    renderLatestMessage,
  } = useIssueCardViewModel(props);

  const severityToVariant: Record<Severity, TypeVariant> = {
    [Severity.Red]: 'attention',
    [Severity.Yellow]: 'blocker',
    [Severity.Green]: 'done',
    [Severity.Purple]: 'note',
    [Severity.Blue]: 'routines',
  };
  const severityVariant = severityToVariant[activity.severity] || 'attention';

  const coverImage = typedCoverImage || activity.asset?.coverImage;
  const coverUri =
    coverImage?.url && `${coverImage.url}`.trim().length > 0 ? coverImage.url : undefined;

  return (
    <View
      style={[
        styles.card,
        styles.cropCard,
        props.isSelected && styles.cardSelected,
        isHovered && styles.cropCardHovered,
        props.borderRadius !== undefined && { borderRadius: props.borderRadius },
      ]}
      {...hoverProps}
    >
      <View style={styles.cropBodyRow}>
        <View style={styles.cropImageWrapper}>
          {coverUri ? (
            <Image source={{ uri: coverUri }} style={styles.cropImage} />
          ) : (
            <View style={styles.cropImagePlaceholder}>
              <Icon name="QR" size={20} color={Colors.grayText} />
            </View>
          )}
        </View>

        <View style={styles.cropContent}>
          <View style={styles.cropWorkspaceRow}>
            <Text style={styles.cropWorkspace} numberOfLines={1} ellipsizeMode="tail">{activity.workspace.name}</Text>
          </View>

          <View style={styles.cropHeader}>
            <View style={styles.assetNamePillWrapper}>
              <Pill
                label={activity.asset?.name || activity.workspace.name}
                icon={QrIcon}
                testID="issue-asset-name-label"
                disabled={true}
              />
            </View>

            <View style={styles.cropMetaRight}>
              <Text variant="bodyMedium" style={styles.cropWhenText}>
                {auditData.when}
              </Text>
              {props.isUnread && <Icon name="Dot" size={12} color={Colors.primary} />}
            </View>
          </View>

          <View style={styles.cropTitleRow}>
            <View style={styles.cropLeadingIcons}>
              <TypeIcon variant={severityVariant} size={16} />
              {activity.nextReminderDate && (
                <View style={styles.cropReminderBadge}>
                  <Icon name="Clock B" size={12} color={Colors.grayText} />
                </View>
              )}
            </View>

            <Text style={styles.cropTitle} numberOfLines={1}>
              {getProcessedTitle()}
            </Text>

            {activity.isPinned && <Icon name="Pin" size={16} color={Colors.grayText} />}
          </View>

          <View style={styles.cropMessageRow}>
            {renderLatestMessage(styles.cropLastMessage, 2)}
          </View>

          {activity.labels && activity.labels.length > 0 && (
            <View style={styles.cropLabelsContainer}>
              {activity.labels.map((label) => (
                <StatusTag key={label.id} variant="neutral" text={label.name} />
              ))}
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

export const IssueCard = (props: IssueCardProps) => {
  // Hooks must be called unconditionally at the top
  const cardRef = useRef<View>(null);
  const [cardHeight, setCardHeight] = useState<number>(200); // Default to 200, will update on layout

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  if (!isCropCircleRedesignEnabled) {
    return <IssueCardOld {...props} />;
  }

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  const cardContent = <IssueCardBase {...props} borderRadius={0} />;

  // Only enable swipeable on mobile platforms
  if (Platform.OS === 'web') {
    return <IssueCardBase {...props} />;
  }

  // Dynamically import SwipeableItem only on mobile to avoid web bundle bloat
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const SwipeableItem = require('react-native-swipeable-item').default;

  // Calculate if reminder is available for dynamic snap points
  const hasReminder = !!props.activity?.nextReminderDate;
  const isReminderType = props.activity?.type === IssueType.Reminder;
  const isReminderAvailable = !hasReminder && !isReminderType;
  // Snap points: cardHeight * 2 for both buttons (reminder + pin), cardHeight for single button (pin only)
  const snapPointsLeft = isReminderAvailable ? [cardHeight * 2] : [cardHeight];

  return (
    <SwipeableItem
      item={props.activity}
      renderUnderlayLeft={(params: any) => <ActionButtonsUnderlay item={params.item} cardHeight={cardHeight} />}
      renderUnderlayRight={(params: any) => <IssueTypeUnderlay item={props.activity} cardHeight={cardHeight} />}
      snapPointsLeft={snapPointsLeft}
      snapPointsRight={[cardHeight]} // Dynamic snap point based on card height
      overSwipe={false}
      key={props.activity.id}
    >
      <View 
        ref={cardRef}
        style={{ borderRadius: 0, overflow: 'visible' }}
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          if (height > 0) {
            setCardHeight(height);
          }
        }}
      >
        {cardContent}
      </View>
    </SwipeableItem>
  );
};

const styles = StyleSheet.create({
  card: {
    width: '100%',
    alignSelf: 'stretch',
    height: 'auto',
    padding: 16,
    ...Platform.select({
      web: { gap: 12 },
      default: { gap: 10 },
    }),
    },
    cropCard: {
    borderRadius: 12,
    backgroundColor: Colors.white,
    paddingVertical: 18,
    paddingHorizontal: 16,
    gap: 12,
    shadowColor: Colors.black,
    shadowOpacity: 0.04,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    ...Platform.select({
      android: {
        elevation: 2,
      },
    }),
  },
  underlayLeft: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  underlayRight: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  issueTypeUnderlayContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0, // Only right: 0, no left constraint to fix margin
    alignItems: 'center',
    justifyContent: 'center',
    // Ensure it has all properties from underlayRight except left
  },
  underlayRightSquare1: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    // right and width are set dynamically based on cardHeight
    backgroundColor: '#2CA9E0', // cyan
  },
  underlayRightSquare2: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    // width is set dynamically based on cardHeight
    backgroundColor: '#FFB368', // orange
  },
  underlayIconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  underlayText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  underlayTextLarge: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  underlayActionButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  cropCardHovered: {
    shadowOpacity: 0.08,
    ...Platform.select({
      android: {
        elevation: 4,
      },
    }),
  },
  cardSelected: {
    backgroundColor: Colors.secondaryGrayHeader,
    borderRadius: 0,
  },
  cropBodyRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  cropImageWrapper: {
    width: 72,
    height: 72,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: Colors.primaryGrayBackground,
    borderWidth: 1,
    borderColor: Colors.graySpacer,
  },
  cropImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  cropImagePlaceholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cropContent: {
    flex: 1,
    gap: 8,
  },
  cropWorkspaceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  cropWorkspace: {
    fontSize: 12,
    fontWeight: '500',
    color: Colors.grayText,
  },
  cropHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  assetNamePillWrapper: {
    marginHorizontal: -8,
    flexShrink: 1,
    flexBasis: 'auto',
    maxWidth: '70%', // Safe constraint to prevent date overlap
  },
  cropMetaRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  cropWhenText: {
    fontSize: 12,
    fontWeight: '500',
    color: Colors.grayText,
  },
  cropTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  cropLeadingIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  cropReminderBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderWidth: 1,
    borderColor: Colors.graySpacer,
  },
  cropTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: Colors.black,
  },
  cropMessageRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cropLastMessage: {
    fontSize: 14,
    fontWeight: '400',
    color: Colors.grayText,
    lineHeight: 20,
    flex: 1,
  },
  cropLabelsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  issueTypeUnderlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0, // Align to right edge - no left constraint
    alignItems: 'center',
    justifyContent: 'center',
  },
  issueTypeUnderlayContent: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  issueTypeUnderlayText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
});
